import React, { useState } from 'react';
import { Play, RotateCcw, Shield, AlertTriangle } from 'lucide-react';

interface SandboxModeProps {
  projectId: string;
  userRole: 'client' | 'vendor' | 'admin';
}

export const SandboxMode: React.FC<SandboxModeProps> = ({ projectId, userRole }) => {
  const [isSandboxActive, setIsSandboxActive] = useState(false);
  const [sandboxData, setSandboxData] = useState({
    tasksCompleted: 0,
    messagesExchanged: 0,
    ticketsCreated: 0,
    timeSpent: 0
  });

  const startSandbox = () => {
    setIsSandboxActive(true);
    setSandboxData({
      tasksCompleted: 0,
      messagesExchanged: 0,
      ticketsCreated: 0,
      timeSpent: 0
    });
  };

  const resetSandbox = () => {
    setSandboxData({
      tasksCompleted: 0,
      messagesExchanged: 0,
      ticketsCreated: 0,
      timeSpent: 0
    });
  };

  const exitSandbox = () => {
    setIsSandboxActive(false);
    resetSandbox();
  };

  if (!isSandboxActive) {
    return (
      <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="w-5 h-5 text-[#4267A1] mr-2" />
          Sandbox Environment
        </h3>
        
        <div className="space-y-4">
          <p className="text-gray-600">
            Try Tyler Technologies onboarding workflows in a safe, isolated environment. 
            All actions are contained within the sandbox and won't affect real project data.
          </p>
          
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">What you can test:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Complete onboarding tasks and workflows</li>
              <li>• Exchange messages with the implementation team</li>
              <li>• Create and manage support tickets</li>
              <li>• Track time and view progress analytics</li>
              <li>• Explore all Tyler Technologies features risk-free</li>
            </ul>
          </div>
          
          <button
            onClick={startSandbox}
            className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg hover:shadow-[#4267A1]/25 transition-all duration-200 font-medium"
          >
            <Play className="w-5 h-5" />
            <span>Start Sandbox</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="p-6 border-2 border-yellow-200 bg-yellow-50 rounded-xl shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-yellow-600" />
            <h3 className="text-lg font-semibold text-yellow-900">Sandbox Mode Active</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={resetSandbox}
              className="flex items-center space-x-2 px-3 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={exitSandbox}
              className="px-3 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Exit Sandbox
            </button>
          </div>
        </div>
        
        <p className="text-yellow-800">
          You are in a safe testing environment. All actions are isolated and won't affect real project data.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-lg">
          <h4 className="font-medium text-gray-900 mb-2">Tasks Completed</h4>
          <div className="text-2xl font-bold text-[#4267A1]">{sandboxData.tasksCompleted}</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-lg">
          <h4 className="font-medium text-gray-900 mb-2">Messages Sent</h4>
          <div className="text-2xl font-bold text-[#8A9150]">{sandboxData.messagesExchanged}</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-lg">
          <h4 className="font-medium text-gray-900 mb-2">Tickets Created</h4>
          <div className="text-2xl font-bold text-purple-600">{sandboxData.ticketsCreated}</div>
        </div>
        <div className="p-4 rounded-xl bg-white border border-gray-200 shadow-lg">
          <h4 className="font-medium text-gray-900 mb-2">Time Spent</h4>
          <div className="text-2xl font-bold text-blue-600">{sandboxData.timeSpent}h</div>
        </div>
      </div>

      <div className="p-6 rounded-xl bg-white border border-gray-200 shadow-lg">
        <h4 className="font-medium text-gray-900 mb-4">Sandbox Actions</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setSandboxData(prev => ({ ...prev, tasksCompleted: prev.tasksCompleted + 1 }))}
            className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
          >
            <h5 className="font-medium text-gray-900 mb-1">Complete a Task</h5>
            <p className="text-sm text-gray-600">Mark a sample onboarding task as complete</p>
          </button>
          
          <button
            onClick={() => setSandboxData(prev => ({ ...prev, messagesExchanged: prev.messagesExchanged + 1 }))}
            className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
          >
            <h5 className="font-medium text-gray-900 mb-1">Send Message</h5>
            <p className="text-sm text-gray-600">Exchange messages with your PM</p>
          </button>
          
          <button
            onClick={() => setSandboxData(prev => ({ ...prev, ticketsCreated: prev.ticketsCreated + 1 }))}
            className="p-4 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-left"
          >
            <h5 className="font-medium text-gray-900 mb-1">Create Ticket</h5>
            <p className="text-sm text-gray-600">Open a support ticket for help</p>
          </button>
        </div>
      </div>
    </div>
  );
};
